import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, StateKey } from '@angular/core';
import { TransferState } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Language, Page, Utils } from 'ctt-babylon';
import {
    BehaviorSubject,
    catchError,
    filter,
    finalize,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { environment } from '../../environments/environment';
import {
    getConsultTypeTransferKey,
    getEngineTransferKey,
    getGeneralConfigTransferKey,
    getPageTransferKey,
    getRoutesTransferKey,
    STATIC_SITE_CONFIG_KEY,
} from '../routes/transfer-keys';
import { WebData } from '../site-config/site-config.interface';
import { ENDPOINTS } from './endpoints';

import sitesData from '../../config/sites.json';
import { LRUCache } from 'lru-cache';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private static readonly PAGE_CACHE_TTL_MS = 30 * 60 * 1000;
    private static readonly ENGINE_CACHE_TTL_MS = 30 * 60 * 1000;

    private readonly platformId = inject(PLATFORM_ID);
    private readonly transferState = inject(TransferState);
    private router = inject(Router);
    private document = inject(DOCUMENT);
    private config$?: Observable<
        GeneralConfigResponse | HttpResponse<GeneralConfigResponse> | undefined
    >;
    private routes$?: Observable<
        GeneralConfigResponse | HttpResponse<GeneralConfigResponse> | undefined
    >;
    private readonly configByLanguage = new Map<
        number,
        Observable<
            | GeneralConfigResponse
            | HttpResponse<GeneralConfigResponse>
            | undefined
        >
    >();

    private readonly currentPath$ = new BehaviorSubject<string>(
        this.seedRelativePath()
    );
    private readonly pageResponseCache = new LRUCache<
        string,
        { expiresAt: number; value: HttpResponse<any> | any | undefined }
    >({
        max: 100,
        ttl: ApiService.PAGE_CACHE_TTL_MS,
    });

    private readonly engineResponseCache = new LRUCache<
        string,
        { expiresAt: number; value: HttpResponse<any> | any | undefined }
    >({
        max: 100,
        ttl: ApiService.ENGINE_CACHE_TTL_MS,
    });
    private readonly inFlightPageRequests = new Map<
        string,
        Observable<HttpResponse<any> | any | undefined>
    >();
    private readonly inFlightEngineRequests = new Map<
        string,
        Observable<HttpResponse<any> | any | undefined>
    >();

    constructor(private readonly http: HttpClient) {}

    // Helper: obtiene la ruta relativa sin host (incluye query/hash) al iniciar
    private seedRelativePath(): string {
        if (isPlatformBrowser(this.platformId) && this.document?.location) {
            const { pathname, search, hash } = this.document.location;
            const base = pathname.startsWith('/') ? pathname : `/${pathname}`;
            return `${base}${search ?? ''}${hash ?? ''}`;
        }
        return '/'; // SSR
    }

    private postFormData(
        data: any,
        url: string
    ): Observable<HttpResponse<void> | undefined | void> {
        return this.post(url, data);
    }

    /**
     * Carga de configuración estática (assets/data):
     * 1) Lee sites.json
     * 2) Resuelve el "site" correcto según la URL actual (relativa o absoluta)
     * 3) Lee el JSON del site resuelto (siteX.json)
     * 4) Cachea con shareReplay(1)
     */
    getStaticConfig(): Observable<
        GeneralConfigResponse | HttpResponse<GeneralConfigResponse> | undefined
    > {
        if (this.config$) {
            return this.config$;
        }

        const transferredConfig = this.consumeTransferState<
            | GeneralConfigResponse
            | HttpResponse<GeneralConfigResponse>
            | undefined
        >(STATIC_SITE_CONFIG_KEY);
        if (transferredConfig !== undefined) {
            this.config$ = of(transferredConfig).pipe(shareReplay(1));
            return this.config$;
        }

        // En browser devolvemos la RUTA RELATIVA normalizada con slash final ("/.../").
        // En SSR devolvemos environment.fallbackUrl (absoluta) para permitir matching por host.

        this.config$ = of(sitesData as any).pipe(
            map((index) =>
                this.findBestSite(index, this.getCurrentOriginWithPath())
            ),
            switchMap((site) =>
                this.http.get<GeneralConfigResponse>(`/data/${site.file}`)
            ),
            tap((config) => {
                this.setTransferState(STATIC_SITE_CONFIG_KEY, config);
            }),
            shareReplay(1),
            catchError((err) => {
                console.warn(
                    '[ApiService] Error loading static config, falling back to API:',
                    err.message
                );
                return of(undefined); // Devolvemos undefined para que salte al catchError del SiteConfigService
            })
        );

        return this.config$;
    }

    getConfig(): Observable<
        GeneralConfigResponse | HttpResponse<GeneralConfigResponse> | undefined
    > {
        let languageId = 1;

        if (isPlatformBrowser(this.platformId)) {
            const language = localStorage.getItem('language');
            languageId = language ? (JSON.parse(language) as Language).id : 1;
        }

        const cachedRequest = this.configByLanguage.get(languageId);
        if (cachedRequest) {
            return cachedRequest;
        }

        const transferKey = getGeneralConfigTransferKey<
            | GeneralConfigResponse
            | HttpResponse<GeneralConfigResponse>
            | undefined
        >(languageId);
        const transferredConfig = this.consumeTransferState<
            | GeneralConfigResponse
            | HttpResponse<GeneralConfigResponse>
            | undefined
        >(transferKey);
        if (transferredConfig !== undefined) {
            const request$ = of(transferredConfig).pipe(
                shareReplay({ bufferSize: 1, refCount: true })
            );
            this.configByLanguage.set(languageId, request$);
            return request$;
        }

        const request$ = this.get<GeneralConfigResponse>(
            `${ENDPOINTS.GENERAL_CONFIG}${environment.siteId}/${languageId}/site`
        ).pipe(
            tap((config) => {
                /* this.setTransferState(transferKey, config); */
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.configByLanguage.set(languageId, request$);
        return request$;
    }

    /**
     * Devuelve:
     * - En browser: **ruta relativa** con slash final ("/", "/purohotels/.../").
     * - En SSR: **URL absoluta** (environment.fallbackUrl).
     * Esto permite un matcher dual (relativo/absoluto) sin tocar sites.json.
     */
    private getCurrentOriginWithPath(): string {
        if (isPlatformBrowser(this.platformId) && this.document?.location) {
            const pathname = this.getRelativeUrlFromRouter();
            const normalized = pathname.endsWith('/')
                ? pathname
                : `${pathname}/`;
            return normalized;
        }
        return environment.fallbackUrl;
    }

    getRoutes(): Observable<
        GeneralConfigResponse | HttpResponse<GeneralConfigResponse> | undefined
    > {
        if (this.routes$) {
            return this.routes$;
        }

        const transferKey = getRoutesTransferKey<
            | GeneralConfigResponse
            | HttpResponse<GeneralConfigResponse>
            | undefined
        >();
        const transferredRoutes = this.consumeTransferState<
            | GeneralConfigResponse
            | HttpResponse<GeneralConfigResponse>
            | undefined
        >(transferKey);
        if (transferredRoutes !== undefined) {
            this.routes$ = of(transferredRoutes).pipe(
                filter((data) => data !== null && data !== undefined),
                map((data) => {
                    return Utils.convertObjectKeysToCamelCase(data);
                }),
                shareReplay({ bufferSize: 1, refCount: true })
            );
            return this.routes$;
        }

        this.routes$ = this.get<GeneralConfigResponse>(
            `${ENDPOINTS.GENERAL_CONFIG}${environment.siteId}/1/site`
        ).pipe(
            tap((routes) => {
                /* this.setTransferState(transferKey, routes); */
            }),
            filter((data) => data !== null && data !== undefined),
            map((data) => {
                return Utils.convertObjectKeysToCamelCase(data);
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        return this.routes$;
    }

    getEngine(
        languageId: string | number | undefined,
        model: string | undefined,
        modelId: number | string | undefined
    ): Observable<any | HttpResponse<any> | undefined> {
        const url = `/front/${environment.siteId}/${model}/${modelId}/${languageId}${ENDPOINTS.ENGINE}`;
        const transferKey = getEngineTransferKey<any>(
            languageId,
            model,
            modelId
        );

        if (!isPlatformBrowser(this.platformId)) {
            return this.get<any>(url).pipe(
                tap((engine) => {
                    this.setTransferState(transferKey, engine);
                })
            );
        }

        const transferredEngine = this.consumeTransferState<any>(transferKey);
        if (transferredEngine !== undefined) {
            const cacheKey = ['engine', languageId, model, modelId].join('|');
            this.engineResponseCache.set(cacheKey, {
                expiresAt: Date.now() + ApiService.ENGINE_CACHE_TTL_MS,
                value: transferredEngine,
            });
            return of(transferredEngine);
        }

        const cacheKey = ['engine', languageId, model, modelId].join('|');

        const cachedResponse = this.engineResponseCache.get(cacheKey);
        if (cachedResponse) {
            return of(cachedResponse.value);
        }

        const inFlightRequest = this.inFlightEngineRequests.get(cacheKey);
        if (inFlightRequest) {
            return inFlightRequest;
        }

        const request$ = this.get<any>(url).pipe(
            tap((response) => {
                this.engineResponseCache.set(cacheKey, {
                    expiresAt: Date.now() + ApiService.ENGINE_CACHE_TTL_MS,
                    value: response,
                });
                /* this.setTransferState(transferKey, response); */
            }),
            finalize(() => {
                this.inFlightEngineRequests.delete(cacheKey);
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.inFlightEngineRequests.set(cacheKey, request$);
        return request$;
    }

    getPage(
        pageId: string | number | undefined,
        languageId: string | number | undefined,
        itemId?: string | number,
        pageName?: string,
        userInfo?: WebData
    ): Observable<any | HttpResponse<any> | undefined> {
        const url = itemId
            ? `${ENDPOINTS.PAGE}${environment.siteId}/${pageId}/${languageId}/${itemId}`
            : `${ENDPOINTS.PAGE}${environment.siteId}/${pageId}/${languageId}`;
        const queryParams = { referer: pageName };
        const method = userInfo ? 'POST' : 'GET';
        const transferKey = getPageTransferKey<any>(pageId, languageId, itemId);

        const transferredPage = this.consumeTransferState<any>(transferKey);
        if (transferredPage !== undefined) {
            const cacheKey = this.buildPageCacheKey(
                method,
                url,
                queryParams,
                userInfo
            );
            this.pageResponseCache.set(cacheKey, {
                expiresAt: Date.now() + ApiService.PAGE_CACHE_TTL_MS,
                value: transferredPage,
            });

            return of(transferredPage);
        }

        const cacheKey = this.buildPageCacheKey(
            method,
            url,
            queryParams,
            userInfo
        );
        this.pruneExpiredPageCache();

        const cachedResponse = this.pageResponseCache.get(cacheKey);
        if (cachedResponse && cachedResponse.expiresAt > Date.now()) {
            return of(cachedResponse.value);
        }

        const inFlightRequest = this.inFlightPageRequests.get(cacheKey);
        if (inFlightRequest) {
            return inFlightRequest;
        }

        const request$ = (
            userInfo
                ? this.post<any>(url, userInfo, queryParams)
                : this.get<any>(url, queryParams)
        ).pipe(
            tap((response) => {
                this.pageResponseCache.set(cacheKey, {
                    expiresAt: Date.now() + ApiService.PAGE_CACHE_TTL_MS,
                    value: response,
                });
                this.setTransferState(transferKey, response);
            }),
            finalize(() => {
                this.inFlightPageRequests.delete(cacheKey);
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.inFlightPageRequests.set(cacheKey, request$);
        return request$;
    }

    private buildPageCacheKey(
        method: 'GET' | 'POST',
        url: string,
        queryParams?: { [key: string]: any },
        body?: any
    ): string {
        return [
            method,
            url,
            this.serializeForCache(queryParams),
            this.serializeForCache(this.normalizePageCachePayload(body)),
        ].join('|');
    }

    private normalizePageCachePayload(value: unknown): unknown {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return value;
        }

        const payload = { ...(value as Record<string, unknown>) };
        delete payload['visitCounts'];
        delete payload['url'];

        return payload;
    }

    private serializeForCache(value: unknown): string {
        if (value === null || value === undefined) return '';
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }

    private pruneExpiredPageCache(): void {
        /*  this.pruneExpiredCacheEntries(this.pageResponseCache); */
    }

    private consumeTransferState<T>(key: StateKey<T>): T | undefined {
        if (!isPlatformBrowser(this.platformId)) {
            return undefined;
        }

        if (!this.transferState.hasKey(key)) {
            return undefined;
        }

        const value = this.transferState.get(key, undefined as T | undefined);
        this.transferState.remove(key);
        return value;
    }

    private setTransferState<T>(key: StateKey<T>, value: T): void {
        if (isPlatformBrowser(this.platformId)) {
            return;
        }

        this.transferState.set(key, value);
    }

    private pruneExpiredCacheEntries<T extends { expiresAt: number }>(
        cache: Map<string, T>
    ): void {
        /*  const now = Date.now();
        for (const [key, entry] of cache.entries()) {
            if (entry.expiresAt <= now) {
                cache.delete(key);
            }
        } */
    }

    getConsultType(
        languageId: string | number | undefined,
        model: string | undefined
    ): Observable<any | HttpResponse<any> | undefined> {
        const transferKey = getConsultTypeTransferKey<any>(languageId, model);
        const transferredConsultType =
            this.consumeTransferState<any>(transferKey);
        if (transferredConsultType !== undefined) {
            return of(transferredConsultType);
        }
        const request$ = this.get<any>(
            `/${model}/${languageId}${ENDPOINTS.CONSULT_TYPE}`
        ).pipe(
            tap((consultType) => {
                this.setTransferState(transferKey, consultType);
            }),
            catchError((err) => {
                return throwError(() => err);
            })
        );

        return request$;
    }

    postNewsletterForm(
        data: any
    ): Observable<HttpResponse<void> | undefined | void> {
        return this.postFormData(data, ENDPOINTS.NEWSLETTER);
    }

    postContactForm(
        data: any
    ): Observable<HttpResponse<void> | undefined | void> {
        return this.postFormData(data, ENDPOINTS.CONTACT);
    }

    private setQueryParams(queryParams?: {
        [key: string]: any;
    }): HttpParams | undefined {
        let params = new HttpParams();
        if (queryParams) {
            for (const key in queryParams) {
                if (queryParams.hasOwnProperty(key)) {
                    params = params.append(key, queryParams[key]);
                }
            }
            return params;
        }
        return undefined;
    }

    // Método genérico para realizar una solicitud GET
    private get<T>(
        url: string,
        queryParams?: { [key: string]: any },
        transferCache: boolean = true
    ): Observable<HttpResponse<T> | T | undefined> {
        const params = this.setQueryParams(queryParams);

        const fullUrl = environment.apiBaseUrl.concat(url);

        return this.http.get<T>(fullUrl, { params, transferCache }).pipe(
            catchError((err) => {
                return throwError(() => err);
            })
        );
    }

    // Método genérico para realizar una solicitud POST
    private post<T>(
        url: string,
        data: any,
        queryParams?: { [key: string]: any },
        transferCache: boolean = true
    ): Observable<HttpResponse<T> | T | undefined> {
        const params = this.setQueryParams(queryParams);

        const fullUrl = environment.apiBaseUrl.concat(url);

        return this.http
            .post<HttpResponse<T>>(fullUrl, data, { params, transferCache })
            .pipe(
                catchError((err) => {
                    return throwError(() => err);
                })
            );
    }

    //Obtener la ip del usuario
    /*     getUserIp(): Observable<string> {
        return this.http
            .get<{ ip_addr: string }>('https://ifconfig.me/all.json')
            .pipe(map((data) => data.ip_addr));
    } */

    // ================== HELPERS ==================

    /** Última ruta relativa conocida (sin host). Útil para debugs/matchers locales */
    getRelativeUrlFromRouter(): string {
        return this.currentPath$.value;
    }

    /**
     * Resuelve el site activo en dos modos:
     * - URL relativa: coincide por **pathname** (prefijo más largo + desempate).
     * - URL absoluta: coincide por **host + prefijo de ruta** (más específico).
     */
    private findBestSite(index: SitesIndex, currentUrlStr: string) {
        const ensureSlash = (s: string) => (s.endsWith('/') ? s : `${s}/`);
        const isAbsolute = /^https?:\/\//i.test(currentUrlStr);

        if (!isAbsolute) {
            const rel = ensureSlash(currentUrlStr || '/').toLowerCase();
            const chosen = this.matchSiteByRelativePath(index, rel);
            if (!chosen) {
                console.warn(
                    '[GeneralConfig] Local sin match por ruta. Usando primer site.'
                );
                return index.sites[0];
            }
            return chosen;
        }

        // MODO ABSOLUTO (SSR o si se pasa absoluto por config)
        let current: URL;
        try {
            current = new URL(ensureSlash(currentUrlStr));
        } catch {
            console.warn(
                '[GeneralConfig] currentUrl inválida. Usando primer site.'
            );
            return index.sites[0];
        }

        const stripWww = (h: string) => h.replace(/^www\./i, '');
        type Candidate = { site: SitesIndex['sites'][number]; score: number };

        const candidates: Candidate[] = [];
        for (const site of index.sites) {
            (['prod', 'staging', 'dev'] as const).forEach((env) => {
                const raw = (site as any)[env] as string | undefined;
                if (!raw) return;
                try {
                    const u = new URL(ensureSlash(raw));
                    const hostEqual =
                        stripWww(u.host) === stripWww(current.host);
                    const pathPrefix = current.pathname
                        .toLowerCase()
                        .startsWith(u.pathname.toLowerCase());
                    if (hostEqual && pathPrefix) {
                        candidates.push({ site, score: u.pathname.length });
                    }
                } catch {}
            });
        }

        if (candidates.length === 0) {
            console.warn(
                '[GeneralConfig] Sin match por dominio/ruta. Usando primer site.'
            );
            return index.sites[0];
        }

        candidates.sort((a, b) => b.score - a.score);
        return candidates[0].site;
    }

    /**
     * MODO LOCAL/RELATIVO:
     * - Elige el site cuyo **prefijo de pathname** coincida y sea **más largo**.
     * - Desempate si varios tienen "/" como base: gana el que tenga **más entornos** con ese prefijo.
     *   (evita elegir el site incorrecto cuando varios comparten "/")
     */
    private matchSiteByRelativePath(index: SitesIndex, relPath: string) {
        const ensureSlash = (p: string) => (p.endsWith('/') ? p : `${p}/`);
        const path = ensureSlash(relPath).toLowerCase();

        type Row = {
            site: SitesIndex['sites'][number];
            bestPrefix: string;
            bestLen: number;
            exactCount: number;
        };

        const rows: Row[] = index.sites.map((site) => {
            const envUrls = [site.prod, site.staging, site.dev].filter(
                Boolean
            ) as string[];
            const prefixes = envUrls.map((raw) => {
                try {
                    const u = new URL(raw);
                    return ensureSlash(u.pathname.toLowerCase());
                } catch {
                    return '/';
                }
            });

            let bestPrefix = '/';
            let bestLen = 1;
            for (const p of prefixes) {
                if (path.startsWith(p) && p.length > bestLen) {
                    bestPrefix = p;
                    bestLen = p.length;
                }
            }

            const exactCount = prefixes.filter((p) => p === bestPrefix).length;
            return { site, bestPrefix, bestLen, exactCount };
        });

        const valid = rows.filter((r) => path.startsWith(r.bestPrefix));
        if (valid.length === 0) return null;

        valid.sort(
            (a, b) => b.bestLen - a.bestLen || b.exactCount - a.exactCount
        );
        return valid[0].site;
    }

    /** Carga un JSON probando varias bases (fallback). Ej.: file='sites.json' o 'site1.json' */
    private loadJsonWithFallback<T>(file: string): Observable<T> {
        const bases = this.getDataBases();
        let i = 0;

        const tryNext = (): Observable<T> => {
            if (i >= bases.length) {
                return throwError(
                    () =>
                        new Error(
                            `[Config] No se pudo cargar ${file} desde: ${bases.join(', ')}`
                        )
                );
            }
            const url = this.joinUrl(bases[i++], file);
            return this.http.get<T>(url).pipe(
                catchError((err) => {
                    if (err?.status === 404 || err?.status === 0)
                        return tryNext();
                    return throwError(() => err);
                })
            );
        };

        return tryNext();
    }

    private getDataBases(): string[] {
        // 1) ruta pública del server (prod/staging): /data/
        // 2) fallback local (ng serve): /assets/data/
        const local = environment.dataBase ?? '/assets/data/';
        return ['/data/', this.ensureSlash(local)];
    }

    private joinUrl(base: string, file: string): string {
        const b = this.ensureSlash(base);
        return `${b}${file.replace(/^\//, '')}`;
    }
    private ensureSlash(u: string): string {
        return u.endsWith('/') ? u : `${u}/`;
    }
}

export interface GeneralConfigResponse {
    siteId: number;
    name: string;
    commonScripts: {
        [k: string]: string | null;
    };
    cdn: string;
    languages: {
        id: number;
        order: number;
        value: string;
        locate: string;
    }[];
    engine: {
        name: string;
        url: string;
        max_adults: number;
        max_childrens: number;
        max_babies: number;
        only_adults: number;
        max_age_children: number;
        max_age_babies: number;
        max_rooms: number;
        enable_children_age: number;
    };
    pages: Page[];
    favicons: {
        rel: string;
        href: string;
        sizes?: string;
    }[];
    logos: string[];
    socialMedia: {
        name: string;
        url: string;
    }[];
    address: {
        name: string;
        address: string;
        city: string;
        province: string;
        country: string;
        cp: string;
        lat: number | null;
        lon: number | null;
    };
}
export interface SitesIndex {
    sites: {
        id: number;
        name: string;
        prod: string;
        staging: string;
        dev: string;
        file: string;
    }[];
}
